(() => {
  /**
   * Sticky Facets for Shopify Horizon
   * Handles:
   * - Desktop horizontal facets
   * - Mobile facets toggle
   * - AJAX faceted search DOM replacement
   * - Filtering, sorting, pagination, history changes
   * - Theme editor reloads
   */

  const DESKTOP_QUERY = '(min-width: 990px)';
  const MOBILE_QUERY = '(max-width: 989px)';

  const SELECTORS = {
    desktopFacets: '.facets.facets--horizontal',
    mobileToggle: '.facets-toggle',
    collectionContainers: [
      '.facets',
      '.facets-toggle',
      'facet-filters-form',
      'facets-form',
      'collection-filters-form',
      '#ProductGridContainer',
      '.product-grid-container',
      '.collection',
      '.collection-wrapper',
      '.main-collection',
      '.main-collection-product-grid'
    ].join(',')
  };

  const HEADER_SELECTORS = [
    '#shopify-section-header',
    '.shopify-section-group-header-group',
    '.shopify-section-header-sticky',
    'header[role="banner"]',
    'header-component',
    '.header-wrapper'
  ].join(',');

  const desktopMediaQuery = window.matchMedia(DESKTOP_QUERY);
  const mobileMediaQuery = window.matchMedia(MOBILE_QUERY);

  let scrollTicking = false;
  let hardRefreshTimer = null;
  let refreshSequence = 0;
  let resizeObserver = null;

  const desktopState = {
    element: null,
    originalTop: 0,
    originalLeft: 0,
    originalWidth: 0,
    originalHeight: 0,
    measured: false
  };

  const mobileState = {
    element: null,
    parent: null,
    originalTop: 0,
    originalLeft: 0,
    originalWidth: 0,
    originalHeight: 0,
    measured: false
  };

  function isDesktop() {
    return desktopMediaQuery.matches;
  }

  function isMobile() {
    return mobileMediaQuery.matches;
  }

  function getCssVarPx(varName) {
    const value = getComputedStyle(document.documentElement).getPropertyValue(varName);
    const number = parseFloat(value);

    return Number.isFinite(number) ? number : 0;
  }

  function isVisibleAndMeasurable(element) {
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    const styles = window.getComputedStyle(element);

    return (
      rect.width > 0 &&
      rect.height > 0 &&
      styles.display !== 'none' &&
      styles.visibility !== 'hidden'
    );
  }

  function getHeaderOffset() {
    let offset = 0;

    document.querySelectorAll(HEADER_SELECTORS).forEach((header) => {
      const rect = header.getBoundingClientRect();
      const styles = window.getComputedStyle(header);

      const isFixedOrSticky =
        styles.position === 'fixed' ||
        styles.position === 'sticky' ||
        header.matches('#shopify-section-header, .shopify-section-group-header-group, header[role="banner"], header-component, .header-wrapper');

      const isVisible =
        rect.width > 0 &&
        rect.height > 0 &&
        rect.bottom > 0 &&
        rect.top < window.innerHeight;

      if (!isFixedOrSticky || !isVisible) return;

      /**
       * Use visible bottom so mobile shrinking/hiding headers do not create a huge gap.
       */
      const visibleBottom = Math.max(0, Math.min(rect.bottom, rect.height));

      offset = Math.max(offset, visibleBottom);
    });

    if (offset === 0) {
      offset = Math.max(
        getCssVarPx('--header-height'),
        getCssVarPx('--sticky-header-height'),
        getCssVarPx('--header-group-height')
      );
    }

    return Math.round(offset);
  }

  function resetDesktopElement(element = desktopState.element) {
    if (!element) return;

    element.classList.remove('is-fixed-horizontal-facets');
    element.style.removeProperty('--fixed-horizontal-facets-top');
    element.style.removeProperty('--fixed-horizontal-facets-left');
    element.style.removeProperty('--fixed-horizontal-facets-width');
  }

  function resetMobileElement(element = mobileState.element, parent = mobileState.parent) {
    if (element) {
      element.classList.remove('is-fixed-mobile-facets-toggle');
      element.style.removeProperty('--fixed-mobile-facets-toggle-top');
      element.style.removeProperty('--fixed-mobile-facets-toggle-left');
      element.style.removeProperty('--fixed-mobile-facets-toggle-width');
      element.style.removeProperty('--fixed-mobile-facets-toggle-height');
    }

    if (parent) {
      parent.classList.remove('has-fixed-mobile-facets-toggle');
      parent.style.removeProperty('--fixed-mobile-facets-toggle-height');
    }
  }

  function resetAllFixedStates() {
    document.querySelectorAll('.is-fixed-horizontal-facets').forEach((element) => {
      resetDesktopElement(element);
    });

    document.querySelectorAll('.is-fixed-mobile-facets-toggle').forEach((element) => {
      const parent = element.parentElement;
      resetMobileElement(element, parent);
    });

    document.querySelectorAll('.has-fixed-mobile-facets-toggle').forEach((element) => {
      element.classList.remove('has-fixed-mobile-facets-toggle');
      element.style.removeProperty('--fixed-mobile-facets-toggle-height');
    });
  }

  function markDesktopUnmeasured() {
    desktopState.element = null;
    desktopState.originalTop = 0;
    desktopState.originalLeft = 0;
    desktopState.originalWidth = 0;
    desktopState.originalHeight = 0;
    desktopState.measured = false;
  }

  function markMobileUnmeasured() {
    mobileState.element = null;
    mobileState.parent = null;
    mobileState.originalTop = 0;
    mobileState.originalLeft = 0;
    mobileState.originalWidth = 0;
    mobileState.originalHeight = 0;
    mobileState.measured = false;
  }

  function measureDesktopFacets() {
    desktopState.measured = false;

    if (!isDesktop()) {
      resetDesktopElement();
      return;
    }

    const element = document.querySelector(SELECTORS.desktopFacets);

    desktopState.element = element;

    if (!element) return;

    resetDesktopElement(element);

    if (!isVisibleAndMeasurable(element)) return;

    const rect = element.getBoundingClientRect();

    desktopState.originalTop = rect.top + window.scrollY;
    desktopState.originalLeft = rect.left;
    desktopState.originalWidth = rect.width;
    desktopState.originalHeight = rect.height;
    desktopState.measured = true;
  }

  function measureMobileToggle() {
    mobileState.measured = false;

    if (!isMobile()) {
      resetMobileElement();
      return;
    }

    const element = document.querySelector(SELECTORS.mobileToggle);
    const parent = element ? element.parentElement : null;

    mobileState.element = element;
    mobileState.parent = parent;

    if (!element) return;

    resetMobileElement(element, parent);

    if (!isVisibleAndMeasurable(element)) return;

    const rect = element.getBoundingClientRect();

    mobileState.originalTop = rect.top + window.scrollY;
    mobileState.originalLeft = rect.left;
    mobileState.originalWidth = rect.width;
    mobileState.originalHeight = rect.height;
    mobileState.measured = true;
  }

  function updateDesktopFacets() {
    if (!isDesktop()) {
      resetDesktopElement();
      desktopState.measured = false;
      return;
    }

    const currentElement = document.querySelector(SELECTORS.desktopFacets);

    /**
     * Important for AJAX faceted search:
     * If the old element was replaced, re-measure the new one.
     */
    if (
      !currentElement ||
      currentElement !== desktopState.element ||
      !desktopState.measured ||
      !document.documentElement.contains(desktopState.element)
    ) {
      measureDesktopFacets();
    }

    const element = desktopState.element;

    if (!element || !desktopState.measured) {
      resetDesktopElement(element);
      return;
    }

    const headerOffset = getHeaderOffset();
    const shouldFix = window.scrollY + headerOffset >= desktopState.originalTop;

    if (!shouldFix) {
      resetDesktopElement(element);

      /**
       * Re-measure natural position after reset.
       */
      if (isVisibleAndMeasurable(element)) {
        const rect = element.getBoundingClientRect();

        desktopState.originalTop = rect.top + window.scrollY;
        desktopState.originalLeft = rect.left;
        desktopState.originalWidth = rect.width;
        desktopState.originalHeight = rect.height;
        desktopState.measured = true;
      }

      return;
    }

    element.classList.add('is-fixed-horizontal-facets');
    element.style.setProperty('--fixed-horizontal-facets-top', `${headerOffset}px`);
    element.style.setProperty('--fixed-horizontal-facets-left', `${Math.round(desktopState.originalLeft)}px`);
    element.style.setProperty('--fixed-horizontal-facets-width', `${Math.round(desktopState.originalWidth)}px`);
  }

  function updateMobileToggle() {
    if (!isMobile()) {
      resetMobileElement();
      mobileState.measured = false;
      return;
    }

    const currentElement = document.querySelector(SELECTORS.mobileToggle);

    /**
     * Important for AJAX faceted search:
     * If the mobile toggle was replaced, re-measure it.
     */
    if (
      !currentElement ||
      currentElement !== mobileState.element ||
      !mobileState.measured ||
      !document.documentElement.contains(mobileState.element)
    ) {
      measureMobileToggle();
    }

    const element = mobileState.element;
    const parent = mobileState.parent;

    if (!element || !mobileState.measured) {
      resetMobileElement(element, parent);
      return;
    }

    const headerOffset = getHeaderOffset();
    const shouldFix = window.scrollY + headerOffset >= mobileState.originalTop;

    if (!shouldFix) {
      resetMobileElement(element, parent);

      /**
       * Re-measure natural position after reset.
       */
      if (isVisibleAndMeasurable(element)) {
        const rect = element.getBoundingClientRect();

        mobileState.originalTop = rect.top + window.scrollY;
        mobileState.originalLeft = rect.left;
        mobileState.originalWidth = rect.width;
        mobileState.originalHeight = rect.height;
        mobileState.measured = true;
      }

      return;
    }

    element.classList.add('is-fixed-mobile-facets-toggle');
    element.style.setProperty('--fixed-mobile-facets-toggle-top', `${headerOffset}px`);
    element.style.setProperty('--fixed-mobile-facets-toggle-left', `${Math.round(mobileState.originalLeft)}px`);
    element.style.setProperty('--fixed-mobile-facets-toggle-width', `${Math.round(mobileState.originalWidth)}px`);
    element.style.setProperty('--fixed-mobile-facets-toggle-height', `${Math.round(mobileState.originalHeight)}px`);

    /**
     * Prevent layout jump on mobile when toggle becomes fixed.
     */
    if (parent) {
      parent.classList.add('has-fixed-mobile-facets-toggle');
      parent.style.setProperty(
        '--fixed-mobile-facets-toggle-height',
        `${Math.round(mobileState.originalHeight)}px`
      );
    }
  }

  function updateAll() {
    updateDesktopFacets();
    updateMobileToggle();
  }

  function measureAll() {
    measureDesktopFacets();
    measureMobileToggle();
  }

  function requestScrollUpdate() {
    if (scrollTicking) return;

    scrollTicking = true;

    window.requestAnimationFrame(() => {
      scrollTicking = false;
      updateAll();
    });
  }

  function reconnectResizeObserver() {
    if (!window.ResizeObserver) return;

    if (resizeObserver) {
      resizeObserver.disconnect();
    }

    resizeObserver = new ResizeObserver(() => {
      requestHardRefresh('resize-observer');
    });

    const desktopElement = document.querySelector(SELECTORS.desktopFacets);
    const mobileElement = document.querySelector(SELECTORS.mobileToggle);
    const productGrid = document.querySelector('#ProductGridContainer, .product-grid-container, .main-collection-product-grid');

    if (desktopElement) resizeObserver.observe(desktopElement);
    if (mobileElement) resizeObserver.observe(mobileElement);
    if (productGrid) resizeObserver.observe(productGrid);
  }

  function runRefreshStep(sequence) {
    if (sequence !== refreshSequence) return;

    resetAllFixedStates();
    markDesktopUnmeasured();
    markMobileUnmeasured();

    measureAll();
    updateAll();
    reconnectResizeObserver();
  }

  function requestHardRefresh(reason = 'unknown') {
    window.clearTimeout(hardRefreshTimer);

    hardRefreshTimer = window.setTimeout(() => {
      const sequence = ++refreshSequence;

      /**
       * Multiple timings are intentional.
       * Horizon/ajax filtering can replace DOM after fetch, then images/layout settle later.
       */
      [0, 50, 150, 350, 700, 1200].forEach((delay) => {
        window.setTimeout(() => {
          runRefreshStep(sequence);
        }, delay);
      });
    }, 80);
  }

  function mutationTouchesCollection(mutations) {
    return mutations.some((mutation) => {
      const nodes = [...mutation.addedNodes, ...mutation.removedNodes];

      return nodes.some((node) => {
        if (!(node instanceof HTMLElement)) return false;

        return (
          node.matches?.(SELECTORS.collectionContainers) ||
          node.querySelector?.(SELECTORS.collectionContainers)
        );
      });
    });
  }

  function setupMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      if (mutationTouchesCollection(mutations)) {
        requestHardRefresh('mutation-observer');
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function setupFetchObserver() {
    if (!window.fetch || window.__stickyFacetsFetchPatched) return;

    const originalFetch = window.fetch;

    window.fetch = function patchedFetch(...args) {
      const request = args[0];
      const url = typeof request === 'string' ? request : request?.url || '';

      const isCollectionRequest =
        url.includes('/collections/') ||
        url.includes('section_id=') ||
        url.includes('filter.') ||
        url.includes('sort_by=') ||
        url.includes('page=');

      return originalFetch.apply(this, args).then((response) => {
        if (isCollectionRequest) {
          requestHardRefresh('fetch');
        }

        return response;
      });
    };

    window.__stickyFacetsFetchPatched = true;
  }

  function setupXhrObserver() {
    if (!window.XMLHttpRequest || window.__stickyFacetsXhrPatched) return;

    const OriginalXHR = window.XMLHttpRequest;

    function PatchedXHR() {
      const xhr = new OriginalXHR();

      let requestUrl = '';

      const originalOpen = xhr.open;

      xhr.open = function patchedOpen(method, url, ...rest) {
        requestUrl = String(url || '');
        return originalOpen.call(xhr, method, url, ...rest);
      };

      xhr.addEventListener('loadend', () => {
        const isCollectionRequest =
          requestUrl.includes('/collections/') ||
          requestUrl.includes('section_id=') ||
          requestUrl.includes('filter.') ||
          requestUrl.includes('sort_by=') ||
          requestUrl.includes('page=');

        if (isCollectionRequest) {
          requestHardRefresh('xhr');
        }
      });

      return xhr;
    }

    window.XMLHttpRequest = PatchedXHR;
    window.__stickyFacetsXhrPatched = true;
  }

  function setupHistoryObserver() {
    if (window.__stickyFacetsHistoryPatched) return;

    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function patchedPushState(...args) {
      const result = originalPushState.apply(this, args);
      requestHardRefresh('history-pushstate');
      return result;
    };

    history.replaceState = function patchedReplaceState(...args) {
      const result = originalReplaceState.apply(this, args);
      requestHardRefresh('history-replacestate');
      return result;
    };

    window.addEventListener('popstate', () => {
      requestHardRefresh('popstate');
    });

    window.__stickyFacetsHistoryPatched = true;
  }

  function setupInteractionEvents() {
    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;

        if (!(target instanceof HTMLElement)) return;

        if (
          target.closest('.facets') ||
          target.closest('.facets-toggle') ||
          target.closest('facet-filters-form') ||
          target.closest('facets-form') ||
          target.closest('collection-filters-form')
        ) {
          requestHardRefresh('facet-change');
        }
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;

        if (!(target instanceof HTMLElement)) return;

        if (
          target.closest('.facets') ||
          target.closest('.facets-toggle') ||
          target.closest('[data-filter-url]') ||
          target.closest('[data-pagination]') ||
          target.closest('.pagination') ||
          target.closest('a[href*="filter."]') ||
          target.closest('a[href*="sort_by="]') ||
          target.closest('a[href*="page="]')
        ) {
          requestHardRefresh('facet-click');
        }
      },
      true
    );
  }

  function setupThemeEvents() {
    [
      'shopify:section:load',
      'shopify:section:reorder',
      'shopify:section:select',
      'shopify:block:select',
      'shopify:block:deselect',
      'collection:updated',
      'collections:updated',
      'facets:updated',
      'facet:updated',
      'filters:updated',
      'theme:collection:updated',
      'product-grid:updated'
    ].forEach((eventName) => {
      document.addEventListener(eventName, () => {
        requestHardRefresh(eventName);
      });
    });
  }

  function setupViewportEvents() {
    window.addEventListener(
      'scroll',
      () => {
        requestScrollUpdate();
      },
      { passive: true }
    );

    window.addEventListener(
      'resize',
      () => {
        requestHardRefresh('window-resize');
      },
      { passive: true }
    );

    if (desktopMediaQuery.addEventListener) {
      desktopMediaQuery.addEventListener('change', () => {
        requestHardRefresh('desktop-breakpoint-change');
      });
    } else {
      desktopMediaQuery.addListener(() => {
        requestHardRefresh('desktop-breakpoint-change');
      });
    }

    if (mobileMediaQuery.addEventListener) {
      mobileMediaQuery.addEventListener('change', () => {
        requestHardRefresh('mobile-breakpoint-change');
      });
    } else {
      mobileMediaQuery.addListener(() => {
        requestHardRefresh('mobile-breakpoint-change');
      });
    }
  }

  function init() {
    resetAllFixedStates();
    measureAll();
    updateAll();

    reconnectResizeObserver();
    setupMutationObserver();
    setupFetchObserver();
    setupXhrObserver();
    setupHistoryObserver();
    setupInteractionEvents();
    setupThemeEvents();
    setupViewportEvents();

    requestHardRefresh('initial-load');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
